import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../data'
import type { ExecutionPage, StepView } from '../data/repository'
import { Badge, PageHeader, Panel, Section } from './primitives'
import { Link } from './shell/Link'
import { StepCard, outOfBand } from './stepper/StepCard'

/**
 * Running a procedure — a build, an inspection — one step at a time, standing
 * at the bench with a part in one hand.
 *
 * One instruction on screen, as large as it can be; a thin bar showing how far
 * along the job is (every segment tappable, to go back and look); the answer
 * controls under it. Resuming lands on the first unanswered step, because
 * "carry on with this one" is the common case.
 */
const ORIGIN: Readonly<Record<string, string>> = { build: 'Build', work_order: 'Work order', manual: 'Job' }

export function StepperView({ id }: { id: string }): JSX.Element {
  const client = useQueryClient()
  const execution = useQuery({ queryKey: ['execution', id], queryFn: () => repository.execution(id) })
  const [cursor, setCursor] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const answer = useMutation({
    mutationFn: ({ stepId, outcome, note }: { stepId: string; outcome: Parameters<typeof repository.answerStep>[2]; note: string }) =>
      repository.answerStep(id, stepId, outcome, note),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['execution', id] })
      await client.invalidateQueries({ queryKey: ['component'] })
      setCursor((c) => c + 1)
    },
    onError: (e: Error) => setError(strip(e.message)),
  })
  const finish = useMutation({
    mutationFn: () => repository.closeExecution(id),
    onSuccess: async () => {
      setError(null)
      await client.invalidateQueries({ queryKey: ['execution', id] })
      await client.invalidateQueries({ queryKey: ['work'] })
    },
    onError: (e: Error) => setError(strip(e.message)),
  })

  if (execution.isPending) return <p className="muted">Loading…</p>
  if (execution.isError) return <p className="critical">{strip(String(execution.error))}</p>
  const e = execution.data

  const answered = e.steps.filter((s) => s.answer !== null).length
  const done = e.status === 'closed' || e.status === 'cancelled'
  const index = Math.max(0, Math.min(Math.max(cursor, e.steps.findIndex((s) => s.answer === null)), e.steps.length - 1))
  const step = e.steps[index]

  return (
    <div className="page page--narrow">
      <PageHeader
        eyebrow={
          <>
            {ORIGIN[e.origin] ?? 'Job'} · {e.fleetNumber ?? e.componentTypeName} <span className="serial">{e.componentSerial}</span> ·{' '}
            {done ? 'closed' : `${answered} of ${e.steps.length} done`}
          </>
        }
        title={e.procedureName}
        actions={
          <>
            {done ? <Badge tone="healthy">Closed</Badge> : null}
            <Link to={{ name: 'component', id: e.componentId }} className="button">
              Open {e.fleetNumber ?? e.componentTypeName.toLowerCase()}
            </Link>
          </>
        }
      />

      <Progress steps={e.steps} at={done ? -1 : index} onPick={setCursor} />

      {error ? <p className="critical" role="alert">{error}</p> : null}

      {done ? (
        <Closed execution={e} />
      ) : step ? (
        <StepCard key={step.id} step={step} total={e.steps.length} busy={answer.isPending} onAnswer={(outcome, note) => answer.mutate({ stepId: step.id, outcome, note })} />
      ) : null}

      {!done && answered === e.steps.length ? (
        <button className="primary big-action" disabled={finish.isPending} onClick={() => finish.mutate()}>
          Sign off and close
        </button>
      ) : null}

      {!done ? (
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <button className="ghost" onClick={() => setCursor(Math.max(0, index - 1))} disabled={index <= 0}>
            ← Previous step
          </button>
          <button className="ghost" onClick={() => setCursor(Math.min(e.steps.length - 1, index + 1))} disabled={index >= e.steps.length - 1}>
            Next step →
          </button>
        </div>
      ) : null}

      {e.findings.length > 0 ? (
        <Section label="Findings">
          <Panel>
            <div className="row-list">
              {e.findings.map((f, i) => (
                <div key={i} className="row-item">
                  <span>{f.text}</span>
                  <Badge tone={f.severity === 'info' ? 'info' : 'warning'}>{f.severity}</Badge>
                </div>
              ))}
            </div>
          </Panel>
        </Section>
      ) : null}
    </div>
  )
}

/** One thin segment per step: green done, amber out of band, cyan here. Tap one to look back. */
function Progress({ steps, at, onPick }: { steps: readonly StepView[]; at: number; onPick: (i: number) => void }): JSX.Element {
  return (
    <div className="step-bar" role="group" aria-label="Steps">
      {steps.map((s, i) => {
        const state = i === at ? 'here' : s.answer === null ? 'todo' : outOfBand(s) ? 'bad' : 'done'
        return (
          <button
            key={s.id}
            className={`step-bar__seg step-bar__seg--${state}`}
            aria-current={i === at ? 'step' : undefined}
            aria-label={`Step ${s.ordinal}: ${s.text}`}
            title={`${s.ordinal}. ${s.text}`}
            onClick={() => onPick(i)}
          />
        )
      })}
    </div>
  )
}

function Closed({ execution }: { execution: ExecutionPage }): JSX.Element {
  return (
    <Section label={`Closed ${execution.closedAt ? new Date(execution.closedAt).toLocaleString() : ''}`}>
      <Panel>
        <div className="row-list">
          {execution.steps.map((s) => (
            <div key={s.id} className="row-item">
              <span className="row-item__main">
                <span className="row-item__title">
                  {s.ordinal}. {s.text}
                </span>
              </span>
              <span className="row-item__side">{answerText(s)}</span>
            </div>
          ))}
        </div>
      </Panel>
      <p className="muted" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        A correction is a new job, not an edit — this is the record of what was done.
      </p>
    </Section>
  )
}

function answerText(s: StepView): string {
  const a = s.answer
  if (!a) return '—'
  if (a.kind === 'check') return a.checkResult === 'na' ? 'Doesn’t apply' : a.checkResult === 'pass' ? 'Pass' : 'Fail'
  if (a.kind === 'measure') return `${a.value} ${a.unit ?? ''}`
  if (a.kind === 'install' || a.kind === 'remove') return a.childSerial ?? ''
  if (a.kind === 'consume') return `${a.amount}`
  return 'Done'
}

/** The transport puts the status in the message; a person does not need it. */
function strip(message: string): string {
  return message.replace(/^\d{3}:\s*/, '')
}
